import React from "react";

import { Box, Grid, TextField } from "@material-ui/core";
import { Autocomplete as MuiAutocomplete } from "@material-ui/lab";
import { faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FieldArray, useFormikContext } from "formik";
import { useSelector } from "react-redux";

import { SlimButton } from "components/Button";
import { ButtonIcon } from "components/Button/FontAwesomeButton";
import { CollapseHeader, FormCollapse } from "components/Form";
import { LabelledControl, withNamespace } from "components/Form/FormikControls";

import { TrackingCategoryDynamicOptionList } from "constants/billing";
import { FinanceSubModalSection } from "constants/settings";

import { sectionHasErrors } from "lib/form";

import { getXeroTrackingCategories } from "selectors";

import { SectionToggleState, useSectionHelper } from "hooks";
import { useFieldValue } from "hooks/useFormik";

const MAX_TRACKING_CATEGORIES = 2;

const DYNAMIC_PREFIX = "dynamic:";

function buildDynamicValue(v: string): string {
  return `${DYNAMIC_PREFIX}${v}`;
}

function isDynamicValue(v: string): boolean {
  return v.startsWith(DYNAMIC_PREFIX);
}

function extractDynamicValue(v: string): string {
  return v.slice(DYNAMIC_PREFIX.length);
}

interface CombinedTrackingOption {
  value: string;
  label: string;
  group: string;
}

interface TrackingCategoryRowProps {
  index: number;
  prefsFieldName: string;
  onRemove: () => void;
}

function TrackingCategoryRow({
  index,
  prefsFieldName,
  onRemove,
}: TrackingCategoryRowProps) {
  const { setFieldValue } = useFormikContext();
  const trackingCategories = useSelector(
    getXeroTrackingCategories,
  ) as XeroTrackingCategory[];

  const optionFieldName = `${prefsFieldName}[${index}].xeroTrackingCategoryOptionId`;
  const categoryFieldName = `${prefsFieldName}[${index}].xeroTrackingCategoryId`;
  const dynamicOptionFieldName = `${prefsFieldName}[${index}].dynamicOption`;

  const categoryId =
    (useFieldValue(categoryFieldName) as number | null) ?? null;
  const selectedOptionId =
    (useFieldValue(optionFieldName) as number | null) ?? null;
  const selectedDynamicOption =
    (useFieldValue(dynamicOptionFieldName) as string | null) ?? null;

  const categoryOptions = trackingCategories.map(c => ({
    value: c.id,
    label: c.name,
  }));

  const xeroTrackingOptions: CombinedTrackingOption[] = (
    trackingCategories.find(c => c.id === categoryId)?.options ?? []
  ).map(o => ({
    value: String(o.id),
    label: o.name,
    group: "Tracking Options",
  }));

  const dynamicOptions: CombinedTrackingOption[] =
    TrackingCategoryDynamicOptionList.map(o => ({
      value: buildDynamicValue(o.value),
      label: o.label,
      group: "Dynamic Options",
    }));

  const allTrackingOptions = [...dynamicOptions, ...xeroTrackingOptions];

  const currentCombinedValue = selectedDynamicOption
    ? buildDynamicValue(selectedDynamicOption)
    : selectedOptionId != null
      ? String(selectedOptionId)
      : null;

  const currentOption =
    allTrackingOptions.find(o => o.value === currentCombinedValue) ?? null;

  return (
    <Grid container spacing={2} item xs={12} alignItems="flex-start">
      <Grid item xs={12} md={5}>
        <LabelledControl
          label={`Tracking Category ${index + 1}`}
          name={`xeroTrackingCategory${index + 1}`}
          tooltip={undefined}
          required={false}
        >
          <MuiAutocomplete<{ value: number; label: string }>
            options={categoryOptions}
            value={categoryOptions.find(o => o.value === categoryId) ?? null}
            getOptionLabel={o => o.label}
            getOptionSelected={(option, value) =>
              typeof value !== "string" && option.value === value.value
            }
            onChange={(_, selected) => {
              const newCategoryId =
                typeof selected === "object" ? selected?.value ?? null : null;
              setFieldValue(categoryFieldName, newCategoryId);
              setFieldValue(optionFieldName, null);
              setFieldValue(dynamicOptionFieldName, null);
            }}
            renderInput={params => <TextField {...params} />}
          />
        </LabelledControl>
      </Grid>
      <Grid item xs={12} md={5}>
        <LabelledControl
          label={`Tracking Option ${index + 1}`}
          name={`trackingOption${index + 1}`}
          tooltip={undefined}
          required={false}
        >
          <MuiAutocomplete<CombinedTrackingOption>
            options={allTrackingOptions}
            value={currentOption}
            disabled={!categoryId}
            getOptionLabel={o => o.label}
            groupBy={o => o.group}
            getOptionSelected={(option, value) =>
              typeof value !== "string" && option.value === value.value
            }
            onChange={(_, selected) => {
              if (!selected || typeof selected !== "object") {
                setFieldValue(optionFieldName, null);
                setFieldValue(dynamicOptionFieldName, null);
              } else if (isDynamicValue(selected.value)) {
                setFieldValue(optionFieldName, null);
                setFieldValue(
                  dynamicOptionFieldName,
                  extractDynamicValue(selected.value),
                );
              } else {
                setFieldValue(optionFieldName, Number(selected.value));
                setFieldValue(dynamicOptionFieldName, null);
              }
            }}
            renderInput={params => <TextField {...params} />}
          />
        </LabelledControl>
      </Grid>
      <Grid item xs={12} md={2}>
        {/* Ghost spacer matching label height so button aligns with the inputs */}
        <Box style={{ fontSize: "0.9rem", visibility: "hidden" }}>&nbsp;</Box>
        <SlimButton
          onClick={onRemove}
          type="button"
          backgroundColor="deleteRed"
        >
          <ButtonIcon icon={faTrash} />
        </SlimButton>
      </Grid>
    </Grid>
  );
}

export const TrackingCategoriesSettings = ({
  sectionHelper,
  namespace: ns,
}: {
  sectionHelper: SectionToggleState;
  namespace: string;
}) => {
  const { isOpen, onToggle } = useSectionHelper(
    sectionHelper,
    FinanceSubModalSection.TRACKING_CATEGORIES,
  );

  const formikProps = useFormikContext();
  const { submitCount } = formikProps;

  const prefsFieldName = withNamespace(ns, "trackingCategoryPreferences");
  const preferences =
    (useFieldValue(prefsFieldName) as TrackingCategoryPreference[]) ?? [];

  const hasErrors =
    submitCount > 0 && sectionHasErrors(formikProps, [prefsFieldName]);

  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={onToggle}
      hasErrorBadge={hasErrors}
      header={<CollapseHeader title="Tracking Categories" />}
    >
      <FieldArray name={prefsFieldName}>
        {({ push, remove }) => (
          <>
            {preferences.map((_, index) => (
              <TrackingCategoryRow
                key={index}
                index={index}
                prefsFieldName={prefsFieldName}
                onRemove={() => remove(index)}
              />
            ))}
            {preferences.length < MAX_TRACKING_CATEGORIES && (
              <Grid item xs={12}>
                <SlimButton
                  onClick={() =>
                    push({
                      xeroTrackingCategoryOptionId: null,
                      xeroTrackingCategoryId: null,
                      dynamicOption: null,
                    })
                  }
                  type="button"
                >
                  <ButtonIcon icon={faPlus} />
                  &nbsp;Add Tracking Category
                </SlimButton>
              </Grid>
            )}
          </>
        )}
      </FieldArray>
    </FormCollapse>
  );
};
